import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Snackbar,
  Collapse,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  DatabaseObjectViewBody,
  DatabaseObjectViewResponse,
} from "@services/admin/types";
import { apiClient } from "../utils/apiClient";

interface DatabaseObjectViewProps {
  open: boolean;
  onClose: () => void;
  initialTable?: string;
  initialId?: string;
  autoFetch?: boolean; // Automatically fetch data when dialog opens
}

// PostgreSQL tables
const POSTGRES_TABLES = [
  "user",
  "organization",
  "email",
  "extractedData",
  "derivedData",
  "gptRequest",
  "dobbyActionLog",
  "token",
  "userFeedback",
  "shipmentReminder",
];

// MongoDB tables
const MONGO_TABLES = ["shipmentEmail", "shipmentFeed", "emailAutomationPipelineResult", "emailAutomationPipelineConfig"];

const AVAILABLE_TABLES = [...POSTGRES_TABLES, ...MONGO_TABLES];

const TABLE_CONFIGS: Record<
  string,
  { db: "postgres" | "mongo"; fields: string[] }
> = {
  // PostgreSQL tables
  user: {
    db: "postgres",
    fields: ["id", "email", "createdAt", "orgId", "adminSettings"],
  },
  organization: {
    db: "postgres",
    fields: ["id", "name", "createdAt", "updatedAt"],
  },
  email: {
    db: "postgres",
    fields: [
      "id",
      "subject",
      "fromEmail",
      "toEmails",
      "messageId",
      "createdAt",
    ],
  },
  extractedData: {
    db: "postgres",
    fields: ["id", "type", "orgId", "userId", "tags", "createdAt"],
  },
  derivedData: {
    db: "postgres",
    fields: ["id", "type", "name", "orgId", "userId", "tags", "createdAt"],
  },
  gptRequest: {
    db: "postgres",
    fields: ["id", "prompt", "answer", "model", "status", "requestTimestamp"],
  },
  dobbyActionLog: {
    db: "postgres",
    fields: [
      "id",
      "type",
      "memo",
      "userId",
      "orgId",
      "createdAt",
      "shipmentEmailId",
      "shipmentFeedId",
    ],
  },
  token: {
    db: "postgres",
    fields: ["id", "type", "userId", "createdAt", "expiresAt"],
  },
  userFeedback: { db: "postgres", fields: ["id", "userId", "createdAt"] },
  shipmentReminder: {
    db: "postgres",
    fields: ["id", "type", "name", "description", "tags", "orgId", "status", "dueTime", "resolvedTime", "shipmentFeedId", "shipmentEmailId", "createdAt"],
  },
  // MongoDB tables
  shipmentEmail: {
    db: "mongo",
    fields: [
      "id",
      "orgId",
      "userId",
      "tags",
      "emailId",
      "createdAt",
      "shipments",
    ],
  },
  shipmentFeed: {
    db: "mongo",
    fields: [
      "id",
      "name",
      "orgId",
      "userId",
      "tags",
      "createdAt",
      "allShipmentProperties",
      "errors",
    ],
  },
  emailAutomationPipelineResult: {
    db: "mongo",
    fields: [
      "id",
      "configId",
      "configSnapshot",
      "name",
      "testSetId",
      "testSetName",
      "emailIds",
      "results",
      "stepsRun",
      "runAt",
      "workflowId",
      "workflowStatus",
      "processedCount",
      "totalCount",
      "errorMessage",
      "createdAt",
      "updatedAt",
    ],
  },
  emailAutomationPipelineConfig: {
    db: "mongo",
    fields: [
      "id",
      "name",
      "description",
      "categorizationStepConfig",
      "propertyExtractionStepConfig",
      "apiFetchStepConfig",
      "presentationStepConfig",
      "createdAt",
      "updatedAt",
    ],
  },
};

export function DatabaseObjectView({
  open,
  onClose,
  initialTable = "",
  initialId = "",
  autoFetch = false,
}: DatabaseObjectViewProps) {
  const [table, setTable] = useState(initialTable);
  const [objectId, setObjectId] = useState(initialId);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [result, setResult] = useState<DatabaseObjectViewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleFetch = useCallback(async () => {
    if (!table || !objectId) {
      setError("Please select a table and enter an object ID");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestBody: DatabaseObjectViewBody = {
        table,
        id: objectId,
        ...(selectedFields.length > 0 && { fields: selectedFields }),
      };

      const data = await apiClient.post<DatabaseObjectViewResponse>(
        "/admin/database-object/view",
        requestBody
      );

      setResult(data);
    } catch (err: any) {
      setError(err.message || "An error occurred");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [table, objectId, selectedFields]);

  // Auto-fetch when dialog opens if autoFetch is true
  useEffect(() => {
    if (open && autoFetch && initialTable && initialId) {
      setTable(initialTable);
      setObjectId(initialId);
      // For GPT requests and emails, preselect key fields
      if (initialTable === "gptRequest") {
        setSelectedFields([
          "id",
          "prompt",
          "answer",
          "model",
          "status",
          "requestTimestamp",
        ]);
      } else if (initialTable === "email") {
        setSelectedFields([
          "id",
          "subject",
          "fromEmail",
          "toEmails",
          "messageId",
          "createdAt",
        ]);
      } else {
        setSelectedFields([]);
      }
      // Trigger fetch with the new values
      const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
          const requestBody: DatabaseObjectViewBody = {
            table: initialTable,
            id: initialId,
            ...(initialTable === "gptRequest" && {
              fields: [
                "id",
                "prompt",
                "answer",
                "model",
                "status",
                "requestTimestamp",
              ],
            }),
            ...(initialTable === "email" && {
              fields: [
                "id",
                "subject",
                "fromEmail",
                "toEmails",
                "messageId",
                "createdAt",
              ],
            }),
          };

          const data = await apiClient.post<DatabaseObjectViewResponse>(
            "/admin/database-object/view",
            requestBody
          );

          setResult(data);
        } catch (err: any) {
          setError(err.message || "An error occurred");
          setResult(null);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [open, autoFetch, initialTable, initialId]);

  const handleFieldToggle = (field: string) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const handleClose = () => {
    setTable(initialTable);
    setObjectId(initialId);
    setSelectedFields([]);
    setResult(null);
    setError(null);
    setLoading(false);
    onClose();
  };

  const handleCopyValue = async (value: any) => {
    try {
      let textToCopy: string;

      if (value === null || value === undefined) {
        textToCopy = "null";
      } else if (typeof value === "object") {
        textToCopy = JSON.stringify(value, null, 2);
      } else {
        textToCopy = String(value);
      }

      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(true);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const JsonDisplayBox = ({ value, label }: { value: any; label?: string }) => {
    const jsonString = JSON.stringify(value, null, 2);
    const lineCount = jsonString.split("\n").length;
    const shouldShowCollapse = lineCount > 50;
    const [isExpanded, setIsExpanded] = useState(true); // Always start expanded

    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          {label && (
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          )}
          {shouldShowCollapse && (
            <IconButton
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{ p: 0.5 }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          <Tooltip title="Copy JSON">
            <IconButton
              size="small"
              onClick={() => handleCopyValue(value)}
              sx={{ p: 0.5 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Collapse in={isExpanded}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: "grey.50",
              maxHeight: shouldShowCollapse ? "400px" : "none",
              overflow: "auto",
            }}
          >
            <Typography
              component="pre"
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "8px",
                lineHeight: 1.4,
                margin: 0,
                whiteSpace: "pre",
                wordBreak: "break-all",
                overflowWrap: "break-word",
              }}
            >
              {jsonString}
            </Typography>
          </Paper>
        </Collapse>
      </Box>
    );
  };

  const renderValue = (value: any, key?: string): React.ReactNode => {
    if (value === null || value === undefined) {
      return (
        <Typography variant="body2" color="textSecondary">
          null
        </Typography>
      );
    }

    if (typeof value === "boolean") {
      return (
        <Chip
          label={value.toString()}
          color={value ? "success" : "default"}
          size="small"
        />
      );
    }

    if (typeof value === "string") {
      if (key && (key.includes("At") || key.includes("Timestamp"))) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return (
              <Typography variant="body2">{date.toLocaleString()}</Typography>
            );
          }
        } catch {
          // Fall through to default string rendering
        }
      }
      return (
        <Typography variant="body2" whiteSpace="pre">
          {value}
        </Typography>
      );
    }

    if (typeof value === "number") {
      return <Typography variant="body2">{value}</Typography>;
    }

    if (Array.isArray(value) || typeof value === "object") {
      return (
        <JsonDisplayBox
          value={value}
          label={`${Array.isArray(value) ? "Array" : "Object"}`}
        />
      );
    }

    return <Typography variant="body2">{String(value)}</Typography>;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {autoFetch && initialTable === "gptRequest"
          ? "GPT Request Details"
          : "Database Object Viewer"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Table Selection and Object ID Input - Side by Side */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Table</InputLabel>
              <Select
                value={table}
                onChange={(e) => {
                  setTable(e.target.value);
                  setSelectedFields([]);
                  setResult(null);
                  setError(null);
                }}
                label="Table"
              >
                {AVAILABLE_TABLES.map((tableName) => (
                  <MenuItem key={tableName} value={tableName}>
                    {tableName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Object ID"
              value={objectId}
              onChange={(e) => setObjectId(e.target.value)}
              placeholder="Enter the UUID of the object to fetch"
            />
          </Box>

          {/* Field Selection */}
          {table && TABLE_CONFIGS[table] && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Fields (leave empty for all fields):
                <Chip
                  size="small"
                  label={TABLE_CONFIGS[table].db.toUpperCase()}
                  color={
                    TABLE_CONFIGS[table].db === "mongo"
                      ? "secondary"
                      : "primary"
                  }
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {TABLE_CONFIGS[table].fields.map((field) => (
                  <Chip
                    key={field}
                    label={field}
                    clickable
                    color={
                      selectedFields.includes(field) ? "primary" : "default"
                    }
                    onClick={() => handleFieldToggle(field)}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Error Display */}
          {error && <Alert severity="error">{error}</Alert>}

          {/* Results Display */}
          {result && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {result.table} (ID: {result.id})
              </Typography>

              {result.object ? (
                <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
                  {Object.entries(result.object).map(([key, value]) => {
                    const isComplexValue =
                      Array.isArray(value) ||
                      (typeof value === "object" && value !== null);

                    return (
                      <Box key={key} sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: isComplexValue
                              ? "flex-start"
                              : "center",
                            gap: 1,
                            mb: isComplexValue ? 1 : 0,
                          }}
                        >
                          <Typography variant="subtitle2" color="primary">
                            {key}:
                          </Typography>
                          <Tooltip title="Copy field value">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyValue(value)}
                              sx={{ padding: 0.5 }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!isComplexValue && (
                            <Box sx={{ flex: 1 }}>
                              {renderValue(value, key)}
                            </Box>
                          )}
                        </Box>
                        {isComplexValue && (
                          <Box sx={{ ml: 2 }}>{renderValue(value, key)}</Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Alert severity="warning">Object not found</Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          onClick={handleFetch}
          variant="contained"
          disabled={loading || !table || !objectId}
        >
          {loading ? <CircularProgress size={20} /> : "Fetch Object"}
        </Button>
      </DialogActions>
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        message="Field value copied to clipboard!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Dialog>
  );
}
